import { useState, useRef, useEffect, useMemo } from 'react'
import { useFormStore, useFormActions, useFormData, useSigningSession, useSignatures, useGeneratedDocuments } from '@hooks/useFormStore'
import { signingApi } from '@services/api'
import { Button } from '@components/UI/Button'
import { getSignatureFields } from '@/types'
import type { SignatureRecord, SignatureFieldConfig } from '@/types'
import styles from './Signing.module.css'

export function SigningSection() {
  const { setStep, addSignature, setSignatures, setSigningSession } = useFormActions()
  const signingSessionId = useFormStore((state) => state.signingSessionId)
  const signingSession = useSigningSession()
  const signatures = useSignatures()
  const generatedDocuments = useGeneratedDocuments()
  const formData = useFormData()

  const signatureFields: SignatureFieldConfig[] = useMemo(() => {
    let contractType = 'cambio_fornitore'
    
    if (generatedDocuments.length > 0) {
      const docType = generatedDocuments[0].type
      if (docType === 'subentro') contractType = 'subentro'
      else if (docType === 'voltura') contractType = 'voltura'
      else contractType = 'cambio_fornitore'
    }
    
    const hasGas = signingSession?.ha_gas ?? false
    const hasLuce = signingSession?.ha_luce ?? false
    const isBusiness = formData.tipo_cliente === 'business'
    // I VAS "Hera NoProblem" li seleziona l'operatore (Adventure) nel riepilogo:
    // qui li leggiamo dalla sessione per aggiungere le firme dedicate in coda.
    const vasGas = (signingSession?.vas_gas ?? false) && hasGas
    const vasLuce = (signingSession?.vas_luce ?? false) && hasLuce
    
    return getSignatureFields(
      contractType, hasGas, hasLuce, isBusiness,
      vasGas, vasLuce
    )
  }, [generatedDocuments, signingSession, formData.tipo_cliente])
  
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0)
  const [isSigningField, setIsSigningField] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCondizioni, setSelectedCondizioni] = useState<'luce' | 'gas' | null>(null)
  
  const fieldRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Carica sessione e firme se non presenti (utile dopo hard refresh)
  useEffect(() => {
    const loadSessionAndSignatures = async () => {
      if (!signingSessionId) return
      
      try {
        // Carica sempre la sessione per assicurarsi che sia aggiornata
        const { session } = await signingApi.getSession(signingSessionId)
        setSigningSession(session)
        
        // Carica sempre le firme esistenti per sincronizzare lo stato
        const { signatures: existingSignatures } = await signingApi.getSignatures(signingSessionId)
        
        // Se non ci sono firme esistenti, resetta lo stato
        if (!existingSignatures || existingSignatures.length === 0) {
          setSignatures([])
          setCurrentFieldIndex(0)
          return
        }
        
        // Converti le firme dal formato backend al formato frontend
        const formattedSignatures: SignatureRecord[] = existingSignatures.map((sig: any) => ({
          fieldId: sig.field_id,
          fieldName: sig.field_name,
          signedAt: sig.signed_at,
          clientName: session.client_name || '',
          clientSurname: session.client_surname || '',
          ipAddress: sig.ip_address
        }))
        
        // Sostituisci tutte le firme con quelle caricate dal backend
        // Questo evita problemi di sincronizzazione dopo un hard refresh
        setSignatures(formattedSignatures)
        
        // Aggiorna currentFieldIndex per puntare al primo campo non firmato
        const firstUnsignedIndex = signatureFields.findIndex(field => 
          !formattedSignatures.some(sig => sig.fieldId === field.id)
        )
        if (firstUnsignedIndex >= 0) {
          setCurrentFieldIndex(firstUnsignedIndex)
        } else {
          // Tutti i campi sono firmati, vai all'ultimo
          setCurrentFieldIndex(signatureFields.length - 1)
        }
      } catch (err) {
        console.error('Error loading session/signatures:', err)
      }
    }
    loadSessionAndSignatures()
  }, [signingSessionId])

  const loadExistingSignatures = async () => {
    if (!signingSessionId) return
    
    try {
      const { signatures: existingSignatures } = await signingApi.getSignatures(signingSessionId)
      
      // Se non ci sono firme esistenti, resetta lo stato
      if (!existingSignatures || existingSignatures.length === 0) {
        setSignatures([])
        setCurrentFieldIndex(0)
        return
      }
      
      // Usa signingSession se disponibile, altrimenti carica la sessione
      let currentSession = signingSession
      if (!currentSession) {
        try {
          const { session } = await signingApi.getSession(signingSessionId)
          currentSession = session
          setSigningSession(session)
        } catch {
          // Ignora errore, useremo valori di default
        }
      }
      
      const formattedSignatures: SignatureRecord[] = existingSignatures.map((sig: any) => ({
        fieldId: sig.field_id,
        fieldName: sig.field_name,
        signedAt: sig.signed_at,
        clientName: currentSession?.client_name || '',
        clientSurname: currentSession?.client_surname || '',
        ipAddress: sig.ip_address
      }))
      
      // Sostituisci tutte le firme con quelle caricate dal backend
      setSignatures(formattedSignatures)
      
      // Aggiorna currentFieldIndex per puntare al primo campo non firmato
      const firstUnsignedIndex = signatureFields.findIndex(field => 
        !formattedSignatures.some(sig => sig.fieldId === field.id)
      )
      if (firstUnsignedIndex >= 0) {
        setCurrentFieldIndex(firstUnsignedIndex)
      } else {
        // Tutti i campi sono firmati, vai all'ultimo
        setCurrentFieldIndex(signatureFields.length - 1)
      }
    } catch (err) {
      console.error('Error loading signatures:', err)
    }
  }

  // Scroll to current field
  const scrollToField = (fieldId: string) => {
    const element = fieldRefs.current.get(fieldId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Handle click and sign
  const handleClickAndSign = () => {
    const currentField = signatureFields[currentFieldIndex]
    if (currentField) {
      scrollToField(currentField.id)
    }
  }

  // Sign a field
  const handleSignField = async (field: { id: string; name: string }) => {
    if (!signingSessionId) {
      setError('Sessione non valida')
      return
    }

    // Verifica se il campo è già firmato prima di procedere
    if (isFieldSigned(field.id)) {
      setError('Campo già firmato')
      // Ricarica le firme per sincronizzare lo stato
      await loadExistingSignatures()
      return
    }

    setIsSigningField(true)
    setError(null)

    try {
      const { signature } = await signingApi.signField(signingSessionId, field.id, field.name)
      
      addSignature(signature)

      // Trova il prossimo campo non firmato
      const nextUnsignedIndex = signatureFields.findIndex((f, idx) => 
        idx > currentFieldIndex && !isFieldSigned(f.id)
      )
      
      if (nextUnsignedIndex >= 0) {
        setCurrentFieldIndex(nextUnsignedIndex)
      } else if (currentFieldIndex < signatureFields.length - 1) {
        setCurrentFieldIndex(prev => prev + 1)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante la firma'
      setError(message)
      
      // Se l'errore è "Campo già firmato", ricarica le firme per sincronizzare
      if (message.includes('già firmato') || message.includes('already signed')) {
        await loadExistingSignatures()
      }
    } finally {
      setIsSigningField(false)
    }
  }

  // Complete signing
  const handleCompleteSigning = async () => {
    if (!signingSessionId) {
      setError('Sessione non valida')
      return
    }

    setIsCompleting(true)
    setError(null)

    try {
      console.log('[SigningSection] Avvio completamento firma per sessione:', signingSessionId)
      
      // Completa la firma - puo richiedere fino a 3 minuti per PDF e SiDial
      const result = await signingApi.completeSigning(signingSessionId)
      console.log('[SigningSection] Completamento riuscito:', result)
      
      // Aggiorna sessione (non blocca se fallisce)
      try {
        const { session } = await signingApi.getSession(signingSessionId)
        setSigningSession(session)
      } catch (sessionErr) {
        console.error('[SigningSection] Errore aggiornamento sessione (non bloccante):', sessionErr)
      }

      // Transizione a completato - sempre eseguita se completeSigning ha successo
      console.log('[SigningSection] Transizione a step completato')
      setStep('completato')
    } catch (err) {
      console.error('[SigningSection] Errore completamento firma:', err)
      
      let message = 'Errore durante il completamento'
      if (err instanceof Error) {
        if (err.message.includes('scaduta') || err.message.includes('timeout')) {
          message = 'La richiesta sta impiegando troppo tempo. Attendere qualche secondo e riprovare.'
        } else {
          message = err.message
        }
      }
      setError(message)
    } finally {
      setIsCompleting(false)
    }
  }

  // Check if field is signed
  const isFieldSigned = (fieldId: string) => {
    return signatures.some(s => s.fieldId === fieldId)
  }

  // Get signature data for field
  const getSignatureData = (fieldId: string): SignatureRecord | undefined => {
    return signatures.find(s => s.fieldId === fieldId)
  }

  // All required fields signed
  const allRequiredFieldsSigned = signatureFields
    .filter(f => f.required)
    .every(f => isFieldSigned(f.id))

  // All fields signed
  const allFieldsSigned = signatureFields.every(f => isFieldSigned(f.id))

  // Get PDF URL for iframe display
  // Use pdf-viewer.php endpoint which has proper headers for iframe embedding
  const getPdfViewerUrl = (url: string) => {
    // Extract filename from URL like /backend/api/v1/generate-pdf/filename.pdf
    const filename = url.split('/').pop()
    return `/backend/pdf-viewer.php?file=${encodeURIComponent(filename || '')}`
  }
  
  const pdfUrl = generatedDocuments.length > 0 
    ? getPdfViewerUrl(generatedDocuments[0].url)
    : signingSession?.pdf_url 
      ? getPdfViewerUrl(signingSession.pdf_url)
      : null

  return (
    <div className={styles.section}>
      {/* Progress Steps */}
      <div className={styles.progressSteps}>
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Richiedi</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Verifica</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
          <div className={styles.progressNumber}>3</div>
          <span className={styles.progressLabel}>Firma</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>4</div>
          <span className={styles.progressLabel}>Completa</span>
        </div>
      </div>

      {/* Header */}
      <div className={styles.signingHeader}>
        <div className={styles.signingHeaderInfo}>
          <i className="bi bi-file-earmark-text" />
          <div>
            <h2 className={styles.signingHeaderTitle}>
              Documento Contratto
            </h2>
            <p className={styles.signingHeaderSubtitle}>
              Firma {signatures.length} di {signatureFields.length} completate
            </p>
          </div>
        </div>
        
        <div className={styles.signingHeaderActions}>
          {!allFieldsSigned ? (
            <Button
              type="button"
              icon="pen"
              onClick={handleClickAndSign}
            >
              Clicca e Firma
            </Button>
          ) : (
            <Button
              type="button"
              icon="check-circle"
              onClick={handleCompleteSigning}
              disabled={isCompleting || !allRequiredFieldsSigned}
              className={styles.successButton}
            >
              {isCompleting ? 'Completamento...' : 'Completa procedura'}
            </Button>
          )}
        </div>
      </div>

      {/* Signature Progress */}
      <div className={styles.signatureProgress}>
        {signatureFields.map((field, index) => (
          <div
            key={field.id}
            className={`${styles.signatureProgressBar} ${
              isFieldSigned(field.id)
                ? 'signed'
                : index === currentFieldIndex
                ? 'current'
                : ''
            }`}
          />
        ))}
      </div>

      {/* PDF Viewer */}
      <div className={styles.pdfViewer}>
        <div className={styles.pdfViewerHeader}>
          <div className={styles.pdfViewerTitle}>
            <i className="bi bi-file-earmark-pdf" />
            <div>
              <h3>Documento Contratto</h3>
              <p>Scorri per leggere il documento completo</p>
            </div>
          </div>
          <span className={styles.pdfBadge}>PDF</span>
        </div>
        
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            className={styles.pdfFrame}
            title="Documento Contratto"
          />
        ) : (
          <div style={{ 
            padding: '3rem', 
            textAlign: 'center', 
            color: 'var(--color-gray-500)',
            background: 'var(--color-gray-50)',
            borderRadius: '0.5rem',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem'
          }}>
            <i className="bi bi-file-earmark-pdf" style={{ fontSize: '3rem', opacity: 0.3 }} />
            <p style={{ fontSize: '1rem', margin: 0 }}>Il documento PDF verrà generato dopo la compilazione del form</p>
          </div>
        )}
        
        <div className={styles.pdfFooter}>
          <p>Documento non modificabile - Leggi attentamente prima di firmare</p>
        </div>
      </div>

      {/* Condizioni Tecnico Economiche */}
      {(signingSession?.ha_luce || signingSession?.ha_gas) && (
        <div className={styles.pdfViewer} style={{ marginTop: '1rem' }}>
          <div className={styles.pdfViewerHeader}>
            <div className={styles.pdfViewerTitle}>
              <i className="bi bi-file-earmark-text" />
              <div>
                <h3>Condizioni Tecnico Economiche</h3>
                <p>Documenti allegati al contratto - Clicca per visualizzare</p>
              </div>
            </div>
          </div>
          
          {/* Tab per selezionare le condizioni */}
          <div style={{ padding: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {signingSession?.ha_luce && (
              <button 
                type="button"
                onClick={() => setSelectedCondizioni(selectedCondizioni === 'luce' ? null : 'luce')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: selectedCondizioni === 'luce' ? 'var(--color-warning-100)' : 'var(--color-gray-50)',
                  borderRadius: '0.5rem',
                  border: selectedCondizioni === 'luce' ? '2px solid var(--color-warning-500)' : '1px solid var(--color-gray-200)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: 1,
                  minWidth: '200px'
                }}
              >
                <i className="bi bi-lightning-charge-fill" style={{ fontSize: '1.25rem', color: 'var(--color-warning-500)' }} />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <strong style={{ display: 'block', fontSize: '0.875rem' }}>Condizioni Luce</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>CTE Hera Hybrid Casa Luce</span>
                </div>
                <i className={`bi ${selectedCondizioni === 'luce' ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: 'var(--color-gray-400)' }} />
              </button>
            )}
            
            {signingSession?.ha_gas && (
              <button 
                type="button"
                onClick={() => setSelectedCondizioni(selectedCondizioni === 'gas' ? null : 'gas')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: selectedCondizioni === 'gas' ? 'var(--color-primary-100)' : 'var(--color-gray-50)',
                  borderRadius: '0.5rem',
                  border: selectedCondizioni === 'gas' ? '2px solid var(--color-primary-500)' : '1px solid var(--color-gray-200)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: 1,
                  minWidth: '200px'
                }}
              >
                <i className="bi bi-fire" style={{ fontSize: '1.25rem', color: 'var(--color-primary-500)' }} />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <strong style={{ display: 'block', fontSize: '0.875rem' }}>Condizioni Gas</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>CTE Hera Hybrid Casa Gas</span>
                </div>
                <i className={`bi ${selectedCondizioni === 'gas' ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: 'var(--color-gray-400)' }} />
              </button>
            )}
          </div>
          
          {/* PDF Viewer per le condizioni selezionate */}
          {selectedCondizioni && (() => {
            const prodId = selectedCondizioni === 'luce'
              ? formData.prodotto_luce_id
              : formData.prodotto_gas_id
            const pathSegment = typeof prodId === 'number' && prodId > 0
              ? String(prodId)
              : selectedCondizioni
            // Se non abbiamo l'ID prodotto e il segmento e' "business", passiamo
            // tipo_cliente=business cosi' il fallback legacy carica le CTE Business.
            const isBusiness = formData.tipo_cliente === 'business'
            const tipoClienteParam = !prodId && isBusiness ? '&tipo_cliente=business' : ''
            return (
              <div style={{ padding: '0 1rem 1rem' }}>
                <iframe
                  src={`/backend/api/v1/condizioni-tecniche/${pathSegment}?inline=1${tipoClienteParam}`}
                  className={styles.pdfFrame}
                  style={{ height: '500px' }}
                  title={`Condizioni Tecniche ${selectedCondizioni === 'luce' ? 'Luce' : 'Gas'}`}
                />
              </div>
            )
          })()}
          
          <div className={styles.pdfFooter}>
            <p>
              {selectedCondizioni 
                ? `Visualizzazione: Condizioni Tecniche Economiche ${selectedCondizioni === 'luce' ? 'Luce' : 'Gas'}`
                : 'Clicca sui pulsanti sopra per visualizzare le condizioni tecniche'}
            </p>
          </div>
        </div>
      )}

      {/* Signature Fields */}
      <div className={styles.signatureFieldsCard}>
        <div className={styles.signatureFieldsHeader}>
          <i className="bi bi-pen" />
          <div>
            <h3 className={styles.signatureFieldsTitle}>Firme e Consensi</h3>
            <p className={styles.signatureFieldsSubtitle}>
              Cliente: <strong>{signingSession?.client_name} {signingSession?.client_surname}</strong>
            </p>
          </div>
        </div>
        
        <div className={styles.signatureFieldsList}>
          {signatureFields.map((field, index) => {
            const signed = isFieldSigned(field.id)
            const signatureData = getSignatureData(field.id)
            const isCurrentField = index === currentFieldIndex && !signed

            return (
              <div
                key={field.id}
                ref={(el) => { if (el) fieldRefs.current.set(field.id, el) }}
                className={`${styles.signatureField} ${
                  signed
                    ? styles.signatureFieldSigned
                    : isCurrentField
                    ? styles.signatureFieldCurrent
                    : ''
                }`}
              >
                <div className={styles.signatureFieldHeader}>
                  <div className={styles.signatureFieldInfo}>
                    <div className={styles.signatureFieldTitle}>
                      {signed ? (
                        <i className="bi bi-check-circle-fill" />
                      ) : field.documentType === 'cte_gas' ? (
                        <i className="bi bi-fire" />
                      ) : field.documentType === 'cte_luce' ? (
                        <i className="bi bi-lightning-charge" />
                      ) : (
                        <i className="bi bi-pen" />
                      )}
                      <span>{field.label}</span>
                      {field.documentType !== 'contratto' && (
                        <span className={styles.cteBadge}>CTE</span>
                      )}
                      {isCurrentField && (
                        <span className={styles.signatureFieldBadge}>Da firmare</span>
                      )}
                    </div>
                    
                    {signed && signatureData ? (
                      <div className={styles.signatureData}>
                        <p className={styles.signatureName}>
                          {signatureData.clientName} {signatureData.clientSurname}
                        </p>
                        <div className={styles.signatureDetails}>
                          <p>Data/Ora: {new Date(signatureData.signedAt).toLocaleString('it-IT')}</p>
                          <p>IP: {signatureData.ipAddress}</p>
                        </div>
                      </div>
                    ) : (
                      <p className={styles.signatureFieldStatus}>
                        {isCurrentField
                          ? 'Clicca il pulsante per firmare questo campo'
                          : 'In attesa di firma'}
                      </p>
                    )}
                  </div>

                  {isCurrentField && !signed && (
                    <button
                      type="button"
                      className={styles.signFieldButton}
                      onClick={() => handleSignField(field)}
                      disabled={isSigningField}
                    >
                      {isSigningField ? (
                        <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <>
                          Firma
                          <i className="bi bi-chevron-right" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className={styles.testModeNotice} style={{ background: 'linear-gradient(145deg, #fee2e2 0%, #fef2f2 100%)', borderColor: '#fca5a5' }}>
          <p style={{ color: '#991b1b' }}>
            <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.5rem' }} />
            {error}
          </p>
        </div>
      )}

      {/* Mobile Fixed Button */}
      <div className={styles.mobileFixedButton}>
        {!allFieldsSigned ? (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleClickAndSign}
          >
            <i className="bi bi-arrow-down" />
            Vai alla prossima firma ({currentFieldIndex + 1}/{signatureFields.length})
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.primaryButton} ${styles.successButton}`}
            onClick={handleCompleteSigning}
            disabled={isCompleting || !allRequiredFieldsSigned}
          >
            {isCompleting ? (
              <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <i className="bi bi-check-circle" />
            )}
            Completa procedura
          </button>
        )}
      </div>

      {/* Spacer for mobile */}
      <div className={styles.mobileSpacer} />

      {/* Navigation - bottone Completa procedura anche su desktop */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={() => setStep('otp_verify')}
          disabled={isSigningField || isCompleting}
        >
          Indietro
        </Button>

        <Button
          type="button"
          icon={isCompleting ? 'arrow-repeat' : 'check-circle'}
          iconPosition="right"
          onClick={handleCompleteSigning}
          disabled={!allFieldsSigned || !allRequiredFieldsSigned || isCompleting}
          className={styles.successButton}
        >
          {isCompleting ? 'Completamento...' : 'Completa procedura'}
        </Button>
      </div>
    </div>
  )
}

